import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import './predictNight.css';
import './slot.css';

// THE SPIN — a hockey slot machine. $25 a spin, one spin per player per day (Montreal date).
// The database spins (spin_slot in supabase-migrations/2026-10-13_slot.sql): it takes the $25,
// rolls the result from LADDER, pays the win and returns the reels; this screen only plays the animation.
// Keep LADDER and the pays the same as the SQL. Opens from a banner on the Games page.
const COST = 25;
const SYMBOLS = ['tor', 'bos', 'mtl', 'nhl', 'star', 'cup'];
const TRIPLE = { cup: 10000, star: 1500, nhl: 500, mtl: 150, bos: 100, tor: 75 };
const TWO_CUPS = 250;
const ONE_CUP = 40;
const PAIR = 25;   // money back
const CONSOLATION = 10;   // no pair, but a McDavid or an NHL shield on the line
const CONSOLE = ['star', 'nhl'];
// the result is rolled first (the rarer, the bigger), then reels are drawn to show it — % of spins
const LADDER = [[10000, 1], [1500, 1.5], [500, 2.5], [250, 3], [150, 4], [100, 5], [75, 6], [40, 10], [25, 27], [10, 30], [0, 10]];
const PIECES = { mid: 34, big: 60, jackpot: 90 };   // confetti on a win of $100 and more
// the art: real NHL logos (the same ones as the rest of the site), McDavid's headshot, the real Stanley Cup
const STAR_ID = 8478402;   // Connor McDavid
const ART = {
  tor: 'https://assets.nhle.com/logos/nhl/svg/TOR_light.svg',
  bos: 'https://assets.nhle.com/logos/nhl/svg/BOS_light.svg',
  mtl: 'https://assets.nhle.com/logos/nhl/svg/MTL_light.svg',
  nhl: 'https://assets.nhle.com/logos/nhl/svg/NHL_light.svg',
  star: `https://assets.nhle.com/mugs/nhl/latest/${STAR_ID}.png`,
  cup: './slot/stanley-cup.png',
};
const NAME = {
  tor: ['Leafs', 'Leafs'], bos: ['Bruins', 'Bruins'], mtl: ['Habs', 'Canadiens'],
  nhl: ['NHL shield', 'Logo LNH'], star: ['McDavid', 'McDavid'], cup: ['Cup', 'Coupe'],
};
const PLURAL = {
  tor: ['Leafs', 'Leafs'], bos: ['Bruins', 'Bruins'], mtl: ['Habs', 'Canadiens'],
  nhl: ['NHL shields', 'logos LNH'], star: ['McDavids', 'McDavid'], cup: ['Cups', 'Coupes'],
};
const today = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Toronto', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
const randomStop = () => SYMBOLS[Math.floor(Math.random() * 6)];
// what a reel shows around its stop (above, the stop, below) — the same before and after a spin
const rowsOf = (reels, i) => [SYMBOLS[(SYMBOLS.indexOf(reels[i]) + 2 + i) % 6], reels[i], SYMBOLS[(SYMBOLS.indexOf(reels[i]) + 4 + i) % 6]];
const reduceMotion = () => typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// the win of a line (best win only) + which reels light up
export function slotWin(r) {
  const cups = r.filter((s) => s === 'cup').length;
  if (r[0] === r[1] && r[1] === r[2]) return { pay: TRIPLE[r[0]], kind: r[0] === 'cup' ? 'jackpot' : 'triple', sym: r[0], hit: [0, 1, 2] };
  if (cups === 2) return { pay: TWO_CUPS, kind: 'cups2', sym: 'cup', hit: [0, 1, 2].filter((i) => r[i] === 'cup') };
  if (cups === 1) return { pay: ONE_CUP, kind: 'cup1', sym: 'cup', hit: [r.indexOf('cup')] };
  const s = r[0] === r[1] || r[0] === r[2] ? r[0] : r[1] === r[2] ? r[1] : null;
  if (s) return { pay: PAIR, kind: 'pair', sym: s, hit: [0, 1, 2].filter((i) => r[i] === s) };
  const c = [0, 1, 2].filter((i) => CONSOLE.includes(r[i]));
  if (c.length) return { pay: CONSOLATION, kind: 'cons', sym: r[c[0]], hit: c };
  return { pay: 0, kind: 'none', sym: null, hit: [] };
}

// DEV ONLY: localhost/?slot=cup (or star, nhl, mtl, bos, tor, cups2, cup1, pair, cons, none, practice) spins
// without the database and lands on that result — to see every animation. Nothing is saved.
const devForce = () => {
  if (process.env.NODE_ENV !== 'development' || typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('slot');
};
const forcedReels = (f) => {
  if (TRIPLE[f]) return [f, f, f];
  if (f === 'cups2') return ['cup', 'tor', 'cup'];
  if (f === 'cup1') return ['tor', 'cup', 'bos'];
  if (f === 'pair') return ['tor', 'bos', 'tor'];
  if (f === 'cons') return ['tor', 'star', 'bos'];
  if (f === 'none') return ['tor', 'bos', 'mtl'];
  return roll();
};
// practice spins: the same roll as slot_roll() in the SQL
function roll() {
  let x = Math.random() * 100;
  const [pay] = LADDER.find(([, pct]) => (x -= pct) < 0) || LADDER[LADDER.length - 1];
  for (;;) {
    const r = [randomStop(), randomStop(), randomStop()];
    if (slotWin(r).pay === pay) return r;
  }
}

// ── the six symbols ──
export function SlotIcon({ sym, className = '' }) {
  return <img className={`sm-icon sm-icon--${sym} ${className}`} src={ART[sym]} alt="" draggable="false" />;
}

// one reel: a strip of symbols that slides down to the result, with a little bounce at the end
const SPIN_STOPS = 26;   // symbols that fly by before the result
function Reel({ index, spin, rest, hit, tease, onStop }) {
  // spin = { id, rows: [above, RESULT, below], dur } while spinning; rest = the 3 symbols shown at rest (above, line, below)
  const [strip, setStrip] = useState(rest);
  const [pos, setPos] = useState({ y: 0, dur: 0, blur: false });
  const shown = useRef(rest);
  useEffect(() => { if (!spin) { setStrip(rest); shown.current = rest; setPos({ y: 0, dur: 0, blur: false }); } }, [rest.join()]); // eslint-disable-line
  useEffect(() => {
    if (!spin) return undefined;
    // [what's shown now] + random stops + [above, RESULT, below]; the strip moves up by all but the last 3
    const end = spin.rows;
    const fly = Array.from({ length: SPIN_STOPS + index * 4 }, randomStop);
    const all = [...shown.current, ...fly, ...end];
    setStrip(all);
    setPos({ y: 0, dur: 0, blur: false });
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => setPos({ y: all.length - 3, dur: spin.dur, blur: true })));
    const unblur = setTimeout(() => setPos((p) => ({ ...p, blur: false })), spin.dur * 0.72);
    const done = setTimeout(() => {
      shown.current = end;
      setStrip(end);
      setPos({ y: 0, dur: 0, blur: false });
      onStop(index);
    }, spin.dur + 40);
    return () => { cancelAnimationFrame(raf); clearTimeout(unblur); clearTimeout(done); };
  }, [spin && spin.id]); // eslint-disable-line
  return (
    <div className={`sm-reel${tease ? ' tease' : ''}`}>
      <div className={`sm-strip${pos.blur ? ' blur' : ''}`}
        style={{ transform: `translateY(calc(var(--cell) * ${-pos.y}))`, transition: pos.dur ? `transform ${pos.dur}ms cubic-bezier(.2, .62, .25, 1.035)` : 'none' }}>
        {strip.map((s, i) => (
          <div key={i} className={`sm-cell${!spin && i === 1 && hit ? ' hit' : ''}`}><SlotIcon sym={s} /></div>
        ))}
      </div>
    </div>
  );
}

export default function SlotMachine({ lang, userId, haptic, balance, onBalance, money }) {
  const L = (en, fr) => (lang === 'fr' ? fr : en);
  const N = (s, n = 1) => (n > 1 ? PLURAL : NAME)[s][lang === 'fr' ? 1 : 0];
  const [open, setOpen] = useState(false);
  const [day, setDay] = useState(today);
  const [spins, setSpins] = useState([]);        // today's spins of everyone: { user_id, reels, payout, created_at }
  const [names, setNames] = useState({});
  const [practice, setPractice] = useState(!!devForce());   // localhost only: spins that cost and pay nothing
  const [missing, setMissing] = useState(false);              // the live site without the database part: no banner at all
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);       // the spin was asked for and the reels haven't stopped yet
  const [spinning, setSpinning] = useState(null); // { id, reels: [...], dur: [...] }
  const [stopped, setStopped] = useState(0);
  const [result, setResult] = useState(null);    // { reels, win, at } once the last reel stopped
  const [practiceSpin, setPracticeSpin] = useState(null);
  const pending = useRef(null);                  // the new balance, shown once the reels stop

  const mine = spins.find((s) => s.user_id === userId);
  const spunToday = !practice && !!mine;
  const rest = useMemo(() => (result ? result.reels : mine ? mine.reels : practiceSpin || ['tor', 'cup', 'bos']), [result, mine, practiceSpin]);
  const restRows = (i) => rowsOf(rest, i);
  const shownWin = result ? result.win : mine ? slotWin(mine.reels) : null;

  // the art loads before the first spin (no blank cells flying by)
  useEffect(() => { Object.values(ART).forEach((src) => { const i = new Image(); i.src = src; }); }, []);

  // ── data ──
  const load = useCallback(async () => {
    if (!userId || devForce()) return;
    const d = today();
    setDay(d);
    const { data, error } = await supabase.from('slot_spins').select('user_id, reels, payout, created_at').eq('spin_date', d).order('created_at', { ascending: false }).limit(500);
    if (error) {
      if (process.env.NODE_ENV !== 'development') { setMissing(true); return; }
      setPractice(true);
      setMsg(L('Practice mode: nothing is saved and nothing is paid — The Spin is not in the database yet.', 'Mode pratique : rien n\'est enregistré ni payé — Le Spin n\'est pas encore dans la base de données.'));
      return;
    }
    setPractice(false);
    setMissing(false);
    setMsg('');
    setSpins(data || []);
    const ids = [...new Set((data || []).map((s) => s.user_id))];
    if (ids.length) {
      const { data: prof } = await supabase.from('profiles').select('id, username').in('id', ids);
      setNames(Object.fromEntries((prof || []).map((p) => [p.id, p.username])));
    }
  }, [userId, lang]); // eslint-disable-line
  // refresh every minute, but never mid-spin (the Today list would give the result away)
  const busyRef = useRef(false);
  busyRef.current = busy;
  useEffect(() => { load(); const id = setInterval(() => { if (!busyRef.current) load(); }, 60 * 1000); return () => clearInterval(id); }, [load]);
  useEffect(() => { const id = setInterval(() => { if (today() !== day) { setResult(null); setPracticeSpin(null); load(); } }, 30000); return () => clearInterval(id); }, [day, load]);

  // ── the spin ──
  const spin = async () => {
    if (busy || spunToday) return;
    setBusy(true);
    setMsg('');
    setResult(null);
    setStopped(0);
    haptic && haptic('tap');
    let reels;
    const f = devForce();
    if (practice || f) {
      reels = forcedReels(f);
      pending.current = null;
    } else {
      const before = balance;
      const { data, error } = await supabase.rpc('spin_slot');
      if (error || !data?.success) {
        setBusy(false);
        setMsg(data?.error === 'funds' ? L('Not enough money in your balance.', 'Pas assez d\'argent dans ton solde.')
          : data?.error === 'already' ? L('You already spun today. Back tomorrow!', 'Tu as déjà joué aujourd\'hui. Reviens demain!')
          : L('The Spin is jammed right now. Try again.', 'Le Spin est bloqué. Réessaie.'));
        if (data?.error === 'already') load();
        return;
      }
      reels = data.reels;
      pending.current = parseFloat(data.new_balance);
      onBalance && onBalance(before - COST);   // the $25 comes off now, the win lands when the reels stop
    }
    // reel 3 takes its time when the first two already promise something
    const tease = reels[0] === reels[1] || (reels[0] === 'cup' && reels[1] === 'cup');
    const quick = reduceMotion();
    setSpinning({ id: Date.now(), reels, tease, dur: quick ? [300, 350, 400] : [1500, 2050, tease ? 3500 : 2600] });
  };

  const onStop = (i) => {
    haptic && haptic('tap');
    setStopped((n) => n + 1);
    if (i !== 2) return;
    const reels = spinning.reels;
    const win = slotWin(reels);
    setSpinning(null);
    setResult({ reels, win, at: Date.now() });
    if (pending.current != null) { onBalance && onBalance(pending.current); pending.current = null; }
    if (practice || devForce()) setPracticeSpin(reels);
    else setSpins((s) => [{ user_id: userId, reels, payout: win.pay, created_at: new Date().toISOString() }, ...s.filter((x) => x.user_id !== userId)]);
    if (win.pay >= 400) haptic && haptic('goal');
    else if (win.pay > 0) haptic && haptic('placed');
    setBusy(false);
  };

  // keyboard (space = spin, escape = close) + body scroll lock while open
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) setOpen(false);
      if ((e.key === ' ' || e.key === 'Enter') && e.target === document.body) { e.preventDefault(); spin(); }
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }); // eslint-disable-line

  const winLabel = (w) => {
    if (!w) return '';
    if (w.kind === 'jackpot') return L('Cup jackpot!', 'Jackpot de la Coupe!');
    if (w.kind === 'triple') return `3 ${N(w.sym, 3)}`;
    if (w.kind === 'cups2') return `2 ${N('cup', 2)}`;
    if (w.kind === 'cup1') return `1 ${N('cup')}`;
    if (w.kind === 'pair') return `${L('Pair of', 'Paire de')} ${N(w.sym, 2)}`;
    if (w.kind === 'cons') return N(w.sym);
    return L('No win', 'Rien cette fois');
  };
  const size = (w) => (!w || !w.pay ? 'none' : w.kind === 'jackpot' ? 'jackpot' : w.pay >= 400 ? 'big' : w.pay >= 100 ? 'mid' : 'small');

  const leaders = useMemo(() => [...spins].sort((a, b) => Number(b.payout) - Number(a.payout) || new Date(a.created_at) - new Date(b.created_at)), [spins]);
  const table = [
    { reels: ['cup', 'cup', 'cup'], pay: TRIPLE.cup, label: L('Cup jackpot', 'Jackpot de la Coupe'), top: true },
    { reels: ['star', 'star', 'star'], pay: TRIPLE.star },
    { reels: ['nhl', 'nhl', 'nhl'], pay: TRIPLE.nhl },
    { reels: ['cup', 'cup'], pay: TWO_CUPS, label: L('Any 2 cups', '2 coupes') },
    { reels: ['mtl', 'mtl', 'mtl'], pay: TRIPLE.mtl },
    { reels: ['bos', 'bos', 'bos'], pay: TRIPLE.bos },
    { reels: ['tor', 'tor', 'tor'], pay: TRIPLE.tor },
    { reels: ['cup'], pay: ONE_CUP, label: L('Any cup', '1 coupe') },
    { reels: ['tor', 'tor'], pay: PAIR, label: L('Any pair', 'Une paire') },
    { reels: ['star', 'nhl'], pay: CONSOLATION, label: L('Any McDavid or shield', 'Un McDavid ou un logo LNH') },
  ];

  if (missing) return null;

  // ── the banner on the Games page ──
  const banner = (
    <div className="pn-banner sm-banner">
      <div className="pn-banner-row">
        <div className="sm-banner-reels" aria-hidden="true">
          {(mine ? mine.reels : ['cup', 'cup', 'cup']).map((s, i) => <span key={i} className="sm-banner-reel"><SlotIcon sym={s} /></span>)}
        </div>
        <div className="pn-banner-text">
          <h3 className="pn-banner-title">{L('The Spin', 'Le Spin')}</h3>
          <div className="pn-banner-chips">
            <span className="pn-chip pn-chip--accent">{L('Jackpot', 'Jackpot')} {money(TRIPLE.cup)}</span>
            <span className="pn-chip">{spunToday ? `${winLabel(slotWin(mine.reels))}${Number(mine.payout) ? ` · +${money(Number(mine.payout))}` : ''}` : L('1 spin a day', '1 tour par jour')}</span>
            {spins.length > 0 && <span className="pn-chip">{spins.length} {L(spins.length === 1 ? 'spin today' : 'spins today', spins.length === 1 ? 'tour aujourd\'hui' : 'tours aujourd\'hui')}</span>}
          </div>
        </div>
        <button type="button" className="pn-banner-cta" onClick={() => setOpen(true)}>
          {spunToday ? L('See today', 'Voir') : `${L('Spin', 'Jouer')} · ${money(COST)}`}
        </button>
      </div>
    </div>
  );
  if (!open) return banner;

  const w = shownWin;
  const sz = result ? size(result.win) : 'none';
  const canSpin = !busy && !spunToday && (practice || balance >= COST);

  return (
    <>
      {banner}
      <div className={`sm sm--${sz}`} role="dialog" aria-modal="true" aria-label={L('The Spin', 'Le Spin')}>
        <div className="sm-bg" aria-hidden="true">
          {(sz === 'big' || sz === 'jackpot') && <div className="sm-beams"><i /><i /></div>}
        </div>

        <div className="pn-top">
          <button type="button" className="pn-icon" onClick={() => !busy && setOpen(false)} aria-label={L('Close', 'Fermer')}>
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
          <span className="sm-balance"><span className="pn-tile pn-tile--white">{L('Balance', 'Solde')}</span><b>{money(balance)}</b></span>
        </div>
        {msg && <div className="pn-msg sm-msg" role="status">{msg}</div>}

        <div className="sm-scroll">
          <div className="sm-main">
            <h2 className="sm-title">{L('The Spin', 'Le Spin')}</h2>

            <div className={`sm-cabinet${busy ? ' spinning' : ''}`}>
              <div className="sm-bulbs sm-bulbs--top" aria-hidden="true">{Array.from({ length: 15 }, (_, i) => <i key={i} />)}</div>
              <div className="sm-jackpot">
                <span className="pn-tile pn-tile--orange">{L('Cup jackpot', 'Jackpot de la Coupe')}</span>
                <b>{money(TRIPLE.cup)}</b>
              </div>
              <div className="sm-window">
                {[0, 1, 2].map((i) => (
                  <Reel key={i} index={i} rest={restRows(i)} onStop={onStop}
                    spin={spinning ? { id: spinning.id, rows: rowsOf(spinning.reels, i), dur: spinning.dur[i] } : null}
                    tease={spinning && spinning.tease && i === 2 && stopped >= 2}
                    hit={!busy && w && w.hit.includes(i)} />
                ))}
                <span className="sm-line sm-line--l" aria-hidden="true" />
                <span className="sm-line sm-line--r" aria-hidden="true" />
                <span className={`sm-payline${!busy && w && w.pay ? ' on' : ''}`} aria-hidden="true" />
              </div>
              <div className="sm-bulbs sm-bulbs--bottom" aria-hidden="true">{Array.from({ length: 15 }, (_, i) => <i key={i} />)}</div>
            </div>

            {/* fixed heights from here down: nothing moves between idle, spinning and the result */}
            <div className="sm-result" aria-live="polite">
              {(result || (spunToday && !busy)) && (() => {
                const win = result ? result.win : slotWin(mine.reels);
                return (
                  <div key={result ? result.at : 'today'} className={`sm-outcome sm-outcome--${result ? sz : 'done'}`}>
                    <span className="sm-outcome-label">{winLabel(win)}</span>
                    <b className={`sm-outcome-pay${win.pay ? '' : ' zero'}`}>{win.pay ? `+${money(win.pay)}` : money(0)}</b>
                  </div>
                );
              })()}
            </div>
            <button type="button" className="pn-tile pn-tile--orange sm-spin" onClick={spin} disabled={!canSpin}>
              {busy ? L('Spinning…', 'Ça tourne…') : spunToday ? L('Back at midnight', 'De retour à minuit') : practice ? L('Spin (practice)', 'Jouer (pratique)') : `${L('Spin', 'Jouer')} · ${money(COST)}`}
            </button>
            <p className="sm-note">
              {!practice && !spunToday && !busy && balance < COST ? L('Your balance is too low to spin.', 'Ton solde est trop bas pour jouer.')
                : spunToday && !busy ? L('Your spin for today is done.', 'Ton tour d\'aujourd\'hui est fait.')
                : L('One spin a day. Best win only.', 'Un tour par jour. Seul le meilleur gain compte.')}
            </p>
          </div>

          <div className="sm-info">
            <section className="sm-panel">
              <h3 className="sm-panel-title">{L('Pays', 'Gains')}</h3>
              {table.map((row, i) => (
                <div key={i} className={`sm-pay${row.top ? ' top' : ''}${w && !busy && w.pay === row.pay ? ' me' : ''}`}>
                  <span className="sm-pay-icons">{row.reels.map((r, j) => <span key={j} className="sm-tile"><SlotIcon sym={r} /></span>)}</span>
                  <span className="sm-pay-name">{row.label || `3 ${N(row.reels[0], 3)}`}</span>
                  <b className="sm-pay-amt">{money(row.pay)}</b>
                </div>
              ))}
              <a className="sm-credit" href="https://commons.wikimedia.org/wiki/File:Stanley_Cup_no_background.png" target="_blank" rel="noopener noreferrer">{L('Stanley Cup photo', 'Photo de la Coupe')}: Alexey Chernyadyev, CC BY 3.0</a>
            </section>
            <section className="sm-panel">
              <h3 className="sm-panel-title">{L('Today', 'Aujourd\'hui')}</h3>
              {leaders.length === 0 ? <p className="sm-empty">{L('Nobody has spun yet today.', 'Personne n\'a joué encore aujourd\'hui.')}</p> : leaders.map((s) => (
                <div key={s.user_id} className={`sm-today${s.user_id === userId ? ' me' : ''}`}>
                  <span className="sm-today-name">{names[s.user_id] || (s.user_id === userId ? L('You', 'Toi') : '—')}</span>
                  <span className="sm-pay-icons">{s.reels.map((r, j) => <span key={j} className="sm-tile"><SlotIcon sym={r} /></span>)}</span>
                  <b className={`sm-today-pay${Number(s.payout) ? '' : ' zero'}`}>{Number(s.payout) ? `+${money(Number(s.payout))}` : '—'}</b>
                </div>
              ))}
            </section>
          </div>
        </div>

        {result && result.win.pay >= 100 && (
          <div className="sm-burst" aria-hidden="true">
            {Array.from({ length: PIECES[sz] }, (_, i) => {
              const a = (i / PIECES[sz]) * 360 + (i % 3) * 9;
              const d = 140 + ((i * 53) % (sz === 'jackpot' ? 420 : 260));
              const c = sz === 'jackpot' ? ['#ffd54a', '#ffffff', '#ffb300'][i % 3] : ['var(--header-accent, #ff5722)', '#ffffff', '#ff3b2f'][i % 3];
              return <i key={i} style={{ background: c, '--dx': `${Math.cos((a * Math.PI) / 180) * d}px`, '--dy': `${Math.sin((a * Math.PI) / 180) * d}px`, animationDelay: `${(i % 6) * 30}ms` }} />;
            })}
          </div>
        )}
        {result && sz === 'jackpot' && (
          <div className="sm-takeover" aria-hidden="true">
            <SlotIcon sym="cup" />
            <span>{L('Cup jackpot', 'Jackpot de la Coupe')}</span>
            <b>+{money(TRIPLE.cup)}</b>
          </div>
        )}
      </div>
    </>
  );
}
